import { type RefObject, useEffect, useRef } from "react";

interface UseTabsKeyboardNavigationOptions {
  containerRef: RefObject<HTMLElement | null>;
  tabPosition: "top" | "bottom" | "left" | "right";
}

/**
 * Keys configured to control focus movement within BuiTabs.
 * These keys move focus between tabs without activating them.
 */
const TabNavigationKey = {
  ArrowLeft: "ArrowLeft",
  ArrowRight: "ArrowRight",
  ArrowUp: "ArrowUp",
  ArrowDown: "ArrowDown",
  Home: "Home",
  End: "End",
} as const;
type TabNavigationKey = keyof typeof TabNavigationKey;
const isTabNavigationKey = (eventKey: string): eventKey is TabNavigationKey =>
  eventKey in TabNavigationKey;

/**
 * Keys configured to activate (select) the currently focused tab.
 */
const TabActivationKey = {
  Enter: "Enter",
} as const;
type TabActivationKey = keyof typeof TabActivationKey;
const isTabActivationKey = (eventKey: string): eventKey is TabActivationKey =>
  eventKey in TabActivationKey;

/**
 * Gets all enabled (non-disabled, visible) tab buttons from the tablist.
 * @param tablist The tablist element
 * @returns Array of enabled tab button elements
 */
function getEnabledTabs(tablist: HTMLElement): HTMLElement[] {
  const allTabs = Array.from(
    tablist.querySelectorAll<HTMLElement>(".ant-tabs-tab"),
  );
  return allTabs
    .filter((tab) => !tab.classList.contains("ant-tabs-tab-disabled"))
    .map((tab) => tab.querySelector<HTMLElement>(".ant-tabs-tab-btn"))
    .filter((btn): btn is HTMLElement => btn !== null);
}

/**
 * Extracts the tab key from a tab button's id attribute.
 * Antd 4.16.12 uses id pattern: "rc-tabs-{prefix}-tab-{key}"
 * @param tabBtn The tab button element
 * @returns The tab key or undefined
 */
function getTabKeyFromId(tabBtn: HTMLElement): string | undefined {
  const id = tabBtn.getAttribute("id");
  if (!id) return undefined;
  // Pattern: rc-tabs-{prefix}-tab-{key}
  const match = id.match(/-tab-(.+)$/);
  return match?.[1];
}

/**
 * Gets the currently active tab key from the DOM by looking at aria-selected attribute.
 * @param tablist The tablist element
 * @returns The active tab key or undefined
 */
function getActiveKeyFromDOM(tablist: HTMLElement): string | undefined {
  const activeTabBtn = tablist.querySelector<HTMLElement>(
    '[aria-selected="true"]',
  );
  return activeTabBtn ? getTabKeyFromId(activeTabBtn) : undefined;
}

/**
 * Applies roving tabindex to tab buttons.
 * Uses focusableKey if provided (for manual keyboard navigation), otherwise uses aria-selected.
 * Only updates attributes when values differ to avoid triggering infinite MutationObserver loops.
 * @param tablist The tablist element
 * @param focusableKey Optional key of the tab that should be focusable (overrides aria-selected)
 */
function applyRovingTabindex(
  tablist: HTMLElement,
  focusableKey?: string | null,
): void {
  // Use focusableKey if provided, otherwise fall back to selected tab
  const targetKey = focusableKey ?? getActiveKeyFromDOM(tablist);
  const allTabBtns = tablist.querySelectorAll<HTMLElement>(".ant-tabs-tab-btn");

  allTabBtns.forEach((btn) => {
    const tabElement = btn.closest<HTMLElement>(".ant-tabs-tab");
    const key = getTabKeyFromId(btn);
    const isFocusable = key === targetKey;
    const isDisabled = tabElement?.classList.contains("ant-tabs-tab-disabled");

    const currentTabindex = btn.getAttribute("tabindex");
    const desiredTabindex = isDisabled ? null : isFocusable ? "0" : "-1";

    // Only update if the value differs to avoid triggering MutationObserver loops
    if (isDisabled && currentTabindex !== null) {
      btn.removeAttribute("tabindex");
    } else if (
      !isDisabled &&
      desiredTabindex !== null &&
      currentTabindex !== desiredTabindex
    ) {
      btn.setAttribute("tabindex", desiredTabindex);
    }
  });
}

/**
 * Custom hook for WAI-ARIA compliant keyboard navigation in BuiTabs.
 * Implements roving tabindex pattern with manual activation:
 * - Arrow keys move focus between tabs (without activating)
 * - Home/End move focus to first/last tab
 * - Enter activates the focused tab
 * - Supports wrap-around and disabled tab skipping
 * @param options - Hook options
 * @param options.containerRef - Ref to the container element
 * @param options.tabPosition - Position of tabs ("top" | "bottom" | "left" | "right").  The alignment of the tabs influences the effects of the keys
 */
export function useTabsKeyboardNavigation({
  containerRef,
  tabPosition,
}: UseTabsKeyboardNavigationOptions) {
  const observerRef = useRef<MutationObserver | null>(null);
  // Tracks the tab key that should be focusable (for keyboard navigation)
  // When set, overrides aria-selected for determining which tab has tabindex="0"
  const focusableKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const tablist = container.querySelector<HTMLElement>('[role="tablist"]');
    if (!tablist) return;

    // Apply initial roving tabindex after a microtask to ensure antd has set aria-selected
    // This timing is required because antd sets tabindex="0" on all tabs initially,
    // and we need aria-selected to determine which tab is active
    queueMicrotask(() => {
      applyRovingTabindex(tablist, focusableKeyRef.current);
    });

    // Re-apply tabindices after antd re-renders using MutationObserver
    // applyRovingTabindex only updates when values differ, preventing infinite loops
    // Re-query tablist in callback to handle cases where antd replaces the element
    observerRef.current = new MutationObserver(() => {
      const currentTablist =
        container.querySelector<HTMLElement>('[role="tablist"]');
      if (currentTablist) {
        applyRovingTabindex(currentTablist, focusableKeyRef.current);
      }
    });

    observerRef.current.observe(tablist, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "aria-selected", "tabindex"],
    });

    const isHorizontal = tabPosition === "top" || tabPosition === "bottom";
    const prevKey = isHorizontal
      ? TabNavigationKey.ArrowLeft
      : TabNavigationKey.ArrowUp;
    const nextKey = isHorizontal
      ? TabNavigationKey.ArrowRight
      : TabNavigationKey.ArrowDown;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      // Only handle events from tab buttons
      if (!target.classList.contains("ant-tabs-tab-btn")) {
        return;
      }

      // Handle activation key (Enter) - activates the focused tab
      if (isTabActivationKey(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        // Clear focusable key - after activation, tabindex follows selection again
        focusableKeyRef.current = null;
        target.click();
        return;
      }

      // Only handle navigation keys
      if (!isTabNavigationKey(event.key)) {
        return;
      }

      // Prevent antd from handling these keys - we manage all navigation
      event.preventDefault();
      event.stopPropagation();

      const enabledTabs = getEnabledTabs(tablist);
      if (enabledTabs.length === 0) return;

      const currentIndex = enabledTabs.indexOf(target);
      if (currentIndex === -1) return;

      let newIndex: number | null = null;

      switch (event.key) {
        case nextKey:
          // Move to next tab, wrap to first if at end
          newIndex = (currentIndex + 1) % enabledTabs.length;
          break;
        case prevKey:
          // Move to previous tab, wrap to last if at start
          newIndex =
            (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
          break;
        case TabNavigationKey.Home:
          newIndex = 0;
          break;
        case TabNavigationKey.End:
          newIndex = enabledTabs.length - 1;
          break;
      }

      if (newIndex !== null && newIndex !== currentIndex) {
        const newTab = enabledTabs[newIndex];
        if (!newTab) return;
        const newTabKey = getTabKeyFromId(newTab);

        // Track the focused tab key so MutationObserver respects it
        focusableKeyRef.current = newTabKey ?? null;

        // Update roving tabindex for the new focused tab
        applyRovingTabindex(tablist, focusableKeyRef.current);

        // Focus the new tab button (without activating it)
        newTab.focus();
      }
    };

    tablist.addEventListener("keydown", handleKeyDown);

    return () => {
      tablist.removeEventListener("keydown", handleKeyDown);
      observerRef.current?.disconnect();
    };
  }, [containerRef, tabPosition]);
}
